import type { UserRole } from '../types/user';

export interface NavItem {
  name: string;
  path: string;
  icon: string; // Icon name as string, resolved via iconMap in components
  roles?: ('student' | 'mentor' | 'admin')[];
}

export interface NavGroup {
  label?: string;
  items: NavItem[];
}

export const publicNavItems: NavItem[] = [
  { name: 'Home', path: '/', icon: 'Home' },
  { name: 'Courses', path: '/courses', icon: 'BookOpen' },
  { name: 'Blogs', path: '/blogs', icon: 'Newspaper' },
  { name: 'Forum', path: '/forum', icon: 'MessageCircle' },
  { name: 'Book Demo', path: '/book-demo', icon: 'Calendar' },
  { name: 'Contact', path: '/contact', icon: 'Phone' },
  { name: 'About', path: '/about', icon: 'Info' },
];

export const dashboardNavGroups: Record<string, NavGroup[]> = {
  student: [
    {
      label: 'Learning',
      items: [
        { name: 'Dashboard', path: '/dashboard/student', icon: 'LayoutDashboard', roles: ['student'] },
        { name: 'My Courses', path: '/dashboard/student/my-courses', icon: 'BookOpen', roles: ['student'] },
        { name: 'Progress', path: '/dashboard/student/progress', icon: 'BarChart2', roles: ['student'] },
        { name: 'Forum', path: '/dashboard/student/forum', icon: 'MessageCircle', roles: ['student'] },
      ],
    },
    {
      label: 'Account',
      items: [
        { name: 'Profile', path: '/dashboard/student/profile', icon: 'User', roles: ['student'] },
        { name: 'Certificates', path: '/dashboard/student/certificates', icon: 'Award', roles: ['student'] },
      ],
    },
  ],
  mentor: [
    {
      label: 'Teaching',
      items: [
        { name: 'Dashboard', path: '/dashboard/mentor', icon: 'LayoutDashboard', roles: ['mentor'] },
        { name: 'My Courses', path: '/dashboard/mentor/manage-courses', icon: 'BookOpen', roles: ['mentor'] },
        { name: 'Create Course', path: '/dashboard/mentor/manage-courses/upload', icon: 'Plus', roles: ['mentor'] },
        { name: 'My Batches', path: '/dashboard/mentor/manage-batches', icon: 'Layers', roles: ['mentor'] },
      ],
    },
    {
      label: 'Students',
      items: [
        { name: 'Student Approvals', path: '/dashboard/mentor/approvals', icon: 'UserCheck', roles: ['mentor'] },
        { name: 'My Students', path: '/dashboard/mentor/students', icon: 'Users', roles: ['mentor'] },
        { name: 'Enrollments', path: '/dashboard/mentor/manage-enrollments', icon: 'GraduationCap', roles: ['mentor'] },
      ],
    },
    {
      label: 'Content',
      items: [
        { name: 'Blogs', path: '/dashboard/mentor/manage-blogs', icon: 'Newspaper', roles: ['mentor'] },
        { name: 'Profile', path: '/dashboard/mentor/profile', icon: 'User', roles: ['mentor'] },
      ],
    },
  ],
  admin: [
    {
      label: 'Overview',
      items: [
        { name: 'Admin Dashboard', path: '/dashboard/admin', icon: 'LayoutDashboard', roles: ['admin'] },
      ],
    },
    {
      label: 'User Management',
      items: [
        { name: 'All Students', path: '/dashboard/admin/students', icon: 'Users', roles: ['admin'] },
        { name: 'Mentors', path: '/dashboard/admin/mentors', icon: 'UserCog', roles: ['admin'] },
        { name: 'Student Approvals', path: '/dashboard/mentor/approvals', icon: 'UserCheck', roles: ['admin'] },
      ],
    },
    {
      label: 'Course Management',
      items: [
        { name: 'All Courses', path: '/dashboard/admin/manage-courses', icon: 'BookOpen', roles: ['admin'] },
        { name: 'Batches', path: '/dashboard/admin/manage-batches', icon: 'Layers', roles: ['admin'] },
        { name: 'Enrollments', path: '/dashboard/admin/manage-enrollments', icon: 'GraduationCap', roles: ['admin'] },
      ],
    },
    {
      label: 'Content',
      items: [
        { name: 'Blogs', path: '/dashboard/admin/manage-blogs', icon: 'Newspaper', roles: ['admin'] },
        { name: 'Create Blog', path: '/dashboard/admin/create-blog', icon: 'Plus', roles: ['admin'] },
        { name: 'Pages', path: '/dashboard/admin/manage-pages', icon: 'Globe', roles: ['admin'] },
      ],
    },
    {
      label: 'Operations',
      items: [
        { name: 'Demo Bookings', path: '/dashboard/admin/demo-bookings', icon: 'CalendarCheck', roles: ['admin'] },
        { name: 'Companies', path: '/dashboard/admin/manage-companies', icon: 'Briefcase', roles: ['admin'] },
      ],
    },
  ],
};

export interface UserMenuItem {
  name: string;
  path?: string;
  action?: 'logout';
  icon: string;
}

export const userMenuItems: UserMenuItem[] = [
  { name: 'Profile', path: '/dashboard/student/profile', icon: 'User' },
  { name: 'Logout', action: 'logout', icon: 'LogOut' },
];

export function getNavGroupsForRole(role: 'student' | 'mentor' | 'admin', isAdmin: boolean): NavGroup[] {
  if (isAdmin) return dashboardNavGroups.admin;
  if (role === 'mentor') return dashboardNavGroups.mentor;
  return dashboardNavGroups.student;
}

export function getDashboardPath(role: 'student' | 'mentor', isAdmin: boolean): string {
  if (isAdmin) return '/dashboard/admin';
  if (role === 'mentor') return '/dashboard/mentor';
  return '/dashboard/student';
}

export function getProfilePath(role: UserRole, isAdmin: boolean): string {
  if (isAdmin) return '/dashboard/admin/profile';
  if (role === 'mentor') return '/dashboard/mentor/profile';
  return '/dashboard/student/profile';
}

export function getDisplayRole(role: string): string {
  return role?.replace('_', ' ') ?? 'Guest';
}
